import * as Sentry from '@sentry/react';
import posthog from 'posthog-js';
import {
  copyEcommercePropertiesToTopLevel,
  determineAffiliation,
  logEventToConsole,
} from '@/analytics/utils';
import { AddonProduct, type AddonProducts } from '@/constants/AddonProduct';
import { AnalyticsEvents } from '@/constants/analytics';
import { getConfig } from '@/constants/config';
import { reregisterDevCycleFlagsOnPostHog } from '@/integrations/devcyclePosthogSync';
import { GTM_PAGE_EVENT, trackGtmEvent } from '@/integrations/gtm';
import { registerBasePostHogSuperProperties } from '@/integrations/posthog/utils';
import { environment } from '@/utils/env.utils';
import { maskSensitiveData } from '@/utils/sensitiveData';
import { generateUUID } from '@/utils/uuid';

// PostHog identify/reset use the global client so these helpers can stay non-hooks.
export function trackUser(id: string) {
  const { country } = getConfig();

  logEventToConsole('identify', { userId: id, country });
  Sentry.setUser({ id });
  try {
    posthog.identify(id, undefined, {
      // Explicitly set the user_id property for easy access in the PostHog dashboard
      user_id: id,
      country,
    });
  } catch (e) {
    Sentry.captureException('Error on PostHog identify', { extra: { error: e } });
  }
}

// Indicates that the user has logged out and should no longer be tracked
export const untrackUser = () => {
  Sentry.setUser(null);
  try {
    posthog.reset();
    // reset() clears super properties — restore them for the new anonymous session
    registerBasePostHogSuperProperties();
    reregisterDevCycleFlagsOnPostHog();
  } catch (e) {
    Sentry.captureException('Error on PostHog reset', { extra: { error: e } });
  }
};

export function trackPageView() {
  logEventToConsole('pageview', { pathname: location.pathname });
  trackGtmEvent(GTM_PAGE_EVENT, {
    path: location.pathname,
    referrer: document.referrer,
    search: location.search,
    title: document.title,
    url: location.href,
    version: environment.version,
    event_id: generateUUID(),
  });
}

export type TrackEventProperties = {
  revenue?: number;
  [key: string]: unknown;
};

export function trackEvent(name: string, props?: TrackEventProperties) {
  const maskedProps = props ? maskSensitiveData(props) : undefined;
  const propsWithEventId = {
    ...maskedProps,
    event_id: typeof maskedProps?.event_id === 'string' ? maskedProps.event_id : generateUUID(),
  };

  logEventToConsole(name, propsWithEventId);
  trackGtmEvent(name, propsWithEventId);
  try {
    posthog.capture(name, propsWithEventId);
  } catch (e) {
    Sentry.captureException(`Error on PostHog capture ${name}`, { extra: { error: e } });
  }
}

type TrackGA4EcommerceEventProperties = TrackEventProperties & {
  ecommerce: Record<string, unknown>;
};

/**
 * Track Google Analytics 4 ecommerce events
 * Automatically adds event_id, affiliation, and copies currency and value from the
 * nested ecommerce object to the top level so GTM receives them at the root level.
 */
function trackGA4EcommerceEvent(name: string, props: TrackGA4EcommerceEventProperties) {
  const payloadWithEventId = {
    ...props,
    event_id: generateUUID(),
  };

  // Add affiliation to ecommerce object if not already present
  const ecommerce = payloadWithEventId.ecommerce;
  if (!('affiliation' in ecommerce && !!ecommerce.affiliation)) {
    ecommerce.affiliation = determineAffiliation();
  }

  const payloadWithTopLevelProps = copyEcommercePropertiesToTopLevel(payloadWithEventId);
  return trackEvent(name, payloadWithTopLevelProps);
}

type ApplicationFinancialEventOptions = Record<string, unknown> & {
  amount: number;
  id: string;
};

// Tracks a financial event keyed by applicationdata_uuid. Currently used in the UK application flow.
export const trackApplicationFinancialEvent = (
  name: string,
  { amount, id, ...restOptions }: ApplicationFinancialEventOptions,
) =>
  trackEvent(name, {
    value: amount,
    revenue: amount, // Needed for reporting to Bing
    currency: getConfig().currency,
    applicationdata_uuid: id,
    ...restOptions,
  });

// https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#begin_checkout
export const trackBeginCheckout = (variant: 'Levla' | 'InitialCredit' | 'Partner') =>
  trackGA4EcommerceEvent('begin_checkout', {
    ecommerce: {
      currency: getConfig().currency,
      value: 0,
      items: [],
    },
    variant,
  });

// Using hardcoded prices because the final prices are not known until the user has paid off their loan.
// The number 1000 is used for both products as a reference because we expect the profit margin to be the same for both products.
const addonProductPrices: Record<AddonProducts, number> = {
  [AddonProduct.ID_PROTECT]: 1000,
  [AddonProduct.PAYMENT_PROTECTION_INSURANCE]: 1000,
};

// https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#add_to_cart
export const trackAddToCart = (addonProduct: AddonProducts) =>
  trackGA4EcommerceEvent('add_to_cart', {
    ecommerce: {
      currency: getConfig().currency,
      value: addonProductPrices[addonProduct],
      items: [
        {
          item_name: addonProduct,
          price: addonProductPrices[addonProduct],
        },
      ],
    },
  });

// https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#remove_from_cart
export const trackRemoveFromCart = (addonProduct: AddonProducts) =>
  trackGA4EcommerceEvent('remove_from_cart', {
    ecommerce: {
      currency: getConfig().currency,
      value: addonProductPrices[addonProduct],
      items: [
        {
          item_name: addonProduct,
          price: addonProductPrices[addonProduct],
        },
      ],
    },
  });

type TrackApplicationGrantedOptions = {
  creditLimit: number;
  addonProducts?: AddonProducts[];
  itemName: 'Credit' | 'Levla' | 'Loan' | 'LoanIdProtect';
};

/**
 * This is not a GA4 ecommerce event, but it is used to track the application granted event.
 */
export const trackApplicationGranted = ({
  creditLimit,
  addonProducts = [],
  itemName,
}: TrackApplicationGrantedOptions) => {
  // Build items array with main product + addon products
  const items: { item_name: string; price: number }[] = [
    {
      item_name: itemName,
      price: creditLimit,
    },
  ];

  addonProducts.forEach(addonProduct => {
    items.push({
      item_name: addonProduct,
      price: addonProductPrices[addonProduct],
    });
  });

  const totalValue = items.reduce((sum, item) => sum + item.price, 0);

  return trackGA4EcommerceEvent('application_granted', {
    ecommerce: {
      value: totalValue,
      currency: getConfig().currency,
      items,
    },
  });
};

/**
 * This is not a GA4 ecommerce event, but it is used to track the application denied event.
 */
export const trackApplicationDenied = ({ reason }: { reason: string }) =>
  trackGA4EcommerceEvent('application_denied', {
    reason,
    ecommerce: {
      value: 0,
      currency: getConfig().currency,
      items: [],
    },
  });

type TrackPurchaseOptions = {
  agreementId: string;
  itemValue: number;
  itemName: 'Credit' | 'Loan' | 'LoanIdProtect' | 'Levla' | 'Partner';
  addonProducts?: AddonProducts[];
};

/**
 * Track purchase event following Enhanced Ecommerce specification.
 * Fires when user successfully completes the application by accepting an offer.
 * https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#purchase
 */
export const trackPurchase = ({
  agreementId,
  itemValue,
  itemName,
  addonProducts = [],
}: TrackPurchaseOptions) => {
  // Build items array with main product + addon products
  const items: { item_name: string; price: number }[] = [
    {
      item_name: itemName,
      price: itemValue,
    },
  ];

  addonProducts.forEach(addonProduct => {
    items.push({
      item_name: addonProduct,
      price: addonProductPrices[addonProduct],
    });
  });

  const totalValue = items.reduce((sum, item) => sum + item.price, 0);

  return trackGA4EcommerceEvent('purchase', {
    ecommerce: {
      transaction_id: agreementId,
      value: totalValue,
      currency: getConfig().currency,
      items,
    },
  });
};

type SEApplicationStep =
  'personal-details' | 'employment-status' | 'living' | 'loan-purpose' | 'addon-products' | 'finances';
type UKApplicationStep =
  | 'personal-details'
  | 'address'
  | 'income'
  | 'outgoings'
  | 'credit-limit'
  | 'mobile-verification'
  | 'documents'
  | 'marketing-consent';

type SubmitCreditApplicationStepOptions = {
  applicantId: string;
  step: SEApplicationStep | UKApplicationStep;
};
export const trackSubmitCreditApplicationStep = (options: SubmitCreditApplicationStepOptions) =>
  trackEvent(AnalyticsEvents.SUBMIT_CREDIT_APPLICATION_STEP, options);
